import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Box,
    Typography,
    Paper,
    CircularProgress,
    Button,
    TextField,
    Grid,
    Divider,
    IconButton,
    List,
    ListItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    SelectChangeEvent,
    Alert,
} from "@mui/material";
import { Add, Delete, ArrowBack, Save } from "@mui/icons-material";
import { URLHandler } from "src/services/URLHandler";
import { useGetOneApplication, useGetApplicationWebhooks } from "src/api/client-api/applications/useGetOneApplication";
import { useCreateWebhook } from "src/api/client-api/applications/useCreateWebhook";
import { useDeleteWebhook } from "src/api/client-api/applications/useDeleteWebhook";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "src/utils/useSnackbar";
import { SecretKeyField } from "./components/SecretKeyField";
import { formatDate } from "src/utils/formatting";

interface Webhook {
    id: number;
    name: string;
    endpoint: string;
    eventName: string;
    active: boolean;
    createdAt?: string;
    method?: string;
    headers?: Record<string, string>;
    retryCount?: number;
}

export const ApplicationEdit = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { setSnackbarProps } = useSnackbar();

    const applicationQuery = useGetOneApplication(id);
    const webhooksQuery = useGetApplicationWebhooks(id);
    const createWebhookMutation = useCreateWebhook();
    const deleteWebhookMutation = useDeleteWebhook();

    const application = applicationQuery.application;
    const isLoadingApp = applicationQuery.isLoading;
    const appError = applicationQuery.error;

    const webhooks = webhooksQuery.webhooks || [];
    const isLoadingWebhooks = webhooksQuery.isLoading;
    const webhooksError = webhooksQuery.error;

    const isCreatingWebhook = createWebhookMutation.isLoading;
    const isDeletingWebhook = deleteWebhookMutation.isLoading;

    const [openDialog, setOpenDialog] = useState(false);
    const [newWebhook, setNewWebhook] = useState<Partial<Webhook>>({
        name: "",
        active: true,
        method: "POST",
        headers: {},
        retryCount: 3,
        eventName: "",
        endpoint: "",
    });
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [headersText, setHeadersText] = useState("{}");
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [webhookToDelete, setWebhookToDelete] = useState<string | null>(null);

    const availableEvents = [
        "application.created",
        "application.updated",
        "application.deleted",
        "webhook.created",
        "webhook.updated",
        "webhook.deleted",
        "user.created",
        "user.updated",
        "user.deleted",
    ];

    const availableMethods = ["POST", "GET", "PUT", "DELETE"];

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
        const { name, value } = e.target;
        if (!name) return;

        let newValue = value;
        if (name === "endpoint" || name === "eventName" || name === "name") {
            newValue = String(value || "").trim();
        } else if (name === "retryCount") {
            newValue = value !== "" ? Number(value) : undefined;
        }

        setNewWebhook((prev) => ({
            ...prev,
            [name]: newValue,
        }));

        if (formErrors[name]) {
            setFormErrors((prev) => ({ ...prev, [name]: "" }));
        }
    };

    const handleEventsChange = (e: SelectChangeEvent<string>) => {
        const value = String(e.target.value || "").trim();
        setNewWebhook((prev) => ({
            ...prev,
            eventName: value,
        }));

        if (formErrors.eventName) {
            setFormErrors((prev) => ({
                ...prev,
                eventName: "",
            }));
        }
    };

    const handleMethodChange = (e: SelectChangeEvent) => {
        setNewWebhook((prev) => ({
            ...prev,
            method: String(e.target.value),
        }));
    };

    const handleHeadersChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setHeadersText(value);

        try {
            const headersObj = JSON.parse(value);
            setNewWebhook((prev) => ({ ...prev, headers: headersObj }));
            if (formErrors.headers) {
                setFormErrors((prev) => ({ ...prev, headers: "" }));
            }
        } catch (err) {
            setFormErrors((prev) => ({ ...prev, headers: t("applications.invalidJson", "Invalid JSON format") }));
        }
    };

    const validateForm = (): boolean => {
        const errors: Record<string, string> = {};

        if (!newWebhook.name) {
            errors.name = t("applications.nameRequired", "Name is required");
        } else if (newWebhook.name.length < 3) {
            errors.name = t("applications.nameTooShort", "Name must be at least 3 characters long");
        } else if (newWebhook.name.length > 255) {
            errors.name = t("applications.nameTooLong", "Name must be 255 characters or less");
        }

        if (!newWebhook.endpoint) {
            errors.endpoint = t("applications.endpointRequired", "Endpoint is required");
        } else if (!/^https?:\/\/.+/.test(newWebhook.endpoint)) {
            errors.endpoint = t("applications.invalidUrl", "Endpoint must be a valid HTTP or HTTPS URL");
        }

        if (!newWebhook.eventName) {
            errors.eventName = t(
                "applications.eventRequired",
                "Event name is required. Select from the Events dropdown.",
            );
        }

        try {
            if (headersText && headersText !== "{}") {
                JSON.parse(headersText);
            }
        } catch (err) {
            errors.headers = t("applications.invalidJson", "Headers must be valid JSON");
        }

        if (
            newWebhook.retryCount !== undefined &&
            (isNaN(Number(newWebhook.retryCount)) || Number(newWebhook.retryCount) < 0)
        ) {
            errors.retryCount = t("applications.invalidRetryCount", "Retry count must be a non-negative number");
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const resetForm = () => {
        setNewWebhook({
            name: "",
            active: true,
            method: "POST",
            headers: {},
            retryCount: 3,
            eventName: "",
            endpoint: "",
        });
        setHeadersText("{}");
        setFormErrors({});
    };

    const handleOpenDialog = () => setOpenDialog(true);
    const handleCloseDialog = () => {
        setOpenDialog(false);
        resetForm();
    };

    const handleCreateWebhook = async () => {
        try {
            setFormErrors({});

            if (!validateForm()) {
                return;
            }

            if (id) {
                // Ensure all string fields are properly formatted
                const name = String(newWebhook.name || "").trim();
                const endpoint = String(newWebhook.endpoint || "").trim();
                const eventName = String(newWebhook.eventName || "").trim();
                const method = String(newWebhook.method || "POST").trim();

                if (!name || !endpoint || !eventName) {
                    const errors: Record<string, string> = {};
                    if (!name) errors.name = t("applications.nameRequired", "Name is required");
                    if (!endpoint) errors.endpoint = t("applications.endpointRequired", "Endpoint is required");
                    if (!eventName) errors.eventName = t("applications.eventRequired", "Event name is required");
                    setFormErrors(errors);
                    return;
                }

                // Create webhook data with proper types - EXACTLY matching API requirements
                const webhookData = {
                    name,
                    endpoint, // Using endpoint instead of url
                    eventName, // Using eventName instead of events array
                    method,
                    headers: newWebhook.headers || {},
                    active: true,
                    retryCount:
                        typeof newWebhook.retryCount === "number" ? Math.min(Math.max(0, newWebhook.retryCount), 5) : 3,
                    application: `/api/integrations/applications/${id}`,
                };

                await createWebhookMutation.mutateAsync({
                    applicationId: id,
                    webhook: webhookData,
                });

                setSnackbarProps({
                    message: t("applications.webhooks.createSuccess", "Webhook created successfully"),
                    type: "success",
                });

                handleCloseDialog();
                webhooksQuery.refetch();
            }
        } catch (error: any) {
            console.error("Error creating webhook:", error);

            if (error && typeof error === "object") {
                setFormErrors(error);
            } else {
                setSnackbarProps({
                    message: t("applications.webhooks.createError", "Failed to create webhook"),
                    type: "error",
                });
            }
        }
    };

    const handleDeleteClick = (webhookId: string) => {
        setWebhookToDelete(webhookId);
        setDeleteConfirmOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!webhookToDelete || !id) return;

        try {
            await deleteWebhookMutation.mutateAsync({ applicationId: id, webhookId: webhookToDelete });

            setSnackbarProps({
                message: t("applications.webhookDeleted", "Webhook deleted successfully"),
                type: "success",
            });

            setDeleteConfirmOpen(false);
            setWebhookToDelete(null);
        } catch (err: any) {
            console.error(`Error deleting webhook ${webhookToDelete}:`, err);

            setSnackbarProps({
                message: t("applications.webhookDeleteError", "Failed to delete webhook"),
                type: "error",
            });
        }
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmOpen(false);
        setWebhookToDelete(null);
    };

    const handleBack = () => navigate(URLHandler.settings.applications.list());

    if (isLoadingApp || isLoadingWebhooks) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (appError || webhooksError) {
        return (
            <Box sx={{ p: 4 }}>
                <Alert severity="error" sx={{ mb: 3 }}>
                    {t("applications.error", "Error")}:{" "}
                    {(appError as Error)?.message || (webhooksError as Error)?.message}
                </Alert>
                <Button variant="outlined" startIcon={<ArrowBack />} onClick={handleBack} sx={{ mt: 2 }}>
                    {t("applications.backToList", "Back to Applications")}
                </Button>
            </Box>
        );
    }

    if (!application) {
        return (
            <Box sx={{ p: 4 }}>
                <Alert severity="warning" sx={{ mb: 3 }}>
                    {t("applications.notFound", "Application not found")}
                </Alert>
                <Button variant="outlined" startIcon={<ArrowBack />} onClick={handleBack} sx={{ mt: 2 }}>
                    {t("applications.backToList", "Back to Applications")}
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 4 }}>
            {/* Header */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <IconButton
                        onClick={handleBack}
                        sx={{ mr: 2 }}
                        aria-label={t("applications.back", "Back to applications list")}
                    >
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h4">
                        {t("applications.editTitle", "Edit Application")}: {application.name}
                    </Typography>
                </Box>
            </Box>

            {/* Application Details */}
            <Paper sx={{ p: 3, mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                    {t("applications.details", "Application Details")}
                </Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                {t("applications.name", "Name")}
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {application.name}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                {t("applications.description", "Description")}
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {application.description || t("applications.noDescription", "No description provided")}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                {t("applications.appId", "App ID")}
                            </Typography>
                            <SecretKeyField secretKey={application.id} />
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                {t("applications.secretKey", "App Secret Key")}
                            </Typography>
                            <SecretKeyField secretKey={application.secretKey} />
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                {t("applications.createdAt", "Created At")}
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {formatDate(application.createdAt)}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* Webhooks Section */}
            <Paper sx={{ p: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Typography variant="h6">{t("applications.webhooks", "Webhooks")}</Typography>
                    <Button variant="contained" color="primary" startIcon={<Add />} onClick={handleOpenDialog}>
                        {t("applications.addWebhook", "Add Webhook")}
                    </Button>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {webhooks.length === 0 ?
                    <Typography variant="body1" sx={{ py: 2, textAlign: "center" }}>
                        {t("applications.noWebhooks", "No webhooks found for this application.")}
                    </Typography>
                :   <List>
                        {webhooks.map((webhook) => (
                            <ListItem
                                key={webhook.id}
                                sx={{
                                    border: "1px solid #e0e0e0",
                                    borderRadius: 1,
                                    mb: 2,
                                    bgcolor: webhook.active ? "background.paper" : "#f5f5f5",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                    padding: 2,
                                    position: "relative",
                                }}
                            >
                                <Box
                                    sx={{
                                        width: "100%",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        mb: 1,
                                    }}
                                >
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        {webhook.name} (ID: {webhook.id})
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        size="small"
                                        startIcon={<Delete />}
                                        onClick={() => handleDeleteClick(webhook.id)}
                                        data-webhook-id={webhook.id}
                                    >
                                        {t("applications.delete", "Delete")}
                                    </Button>
                                </Box>

                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="body2" component="span" display="block" fontWeight="bold">
                                            {t("applications.eventName", "Event Name")}:
                                        </Typography>
                                        <Typography variant="body2" component="span" display="block">
                                            {webhook.eventName || "N/A"}
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <Typography variant="body2" component="span" display="block" fontWeight="bold">
                                            {t("applications.endpoint", "Endpoint")}:
                                        </Typography>
                                        <Typography variant="body2" component="span" display="block">
                                            {webhook.endpoint || "N/A"}
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <Typography variant="body2" component="span" display="block" fontWeight="bold">
                                            {t("applications.method", "Method")}:
                                        </Typography>
                                        <Typography variant="body2" component="span" display="block">
                                            {webhook.method || "POST"}
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <Typography variant="body2" component="span" display="block" fontWeight="bold">
                                            {t("applications.retryCount", "Retry Count")}:
                                        </Typography>
                                        <Typography variant="body2" component="span" display="block">
                                            {webhook.retryCount !== undefined ? webhook.retryCount : "N/A"}
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <Typography variant="body2" component="span" display="block" fontWeight="bold">
                                            {t("applications.status", "Status")}:
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            component="span"
                                            display="block"
                                            color={webhook.active ? "success.main" : "text.secondary"}
                                        >
                                            {webhook.active ?
                                                t("applications.active", "Active")
                                            :   t("applications.inactive", "Inactive")}
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Typography variant="body2" component="span" display="block" fontWeight="bold">
                                            {t("applications.headers", "Headers")}:
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            component="span"
                                            display="block"
                                            sx={{
                                                maxHeight: "100px",
                                                overflowY: "auto",
                                                bgcolor: "#f5f5f5",
                                                p: 1,
                                                borderRadius: 1,
                                                fontFamily: "monospace",
                                            }}
                                        >
                                            {webhook.headers ?
                                                JSON.stringify(webhook.headers, null, 2)
                                            :   t("applications.noHeaders", "No headers")}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </ListItem>
                        ))}
                    </List>
                }
            </Paper>

            {/* Add Webhook Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>{t("applications.addWebhook", "Add New Webhook")}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={3} sx={{ mt: 0 }}>
                        <Grid item xs={12}>
                            <TextField
                                name="name"
                                label={t("applications.webhookName", "Webhook Name")}
                                value={newWebhook.name}
                                onChange={handleInputChange}
                                fullWidth
                                required
                                error={!!formErrors.name}
                                helperText={formErrors.name}
                                disabled={isCreatingWebhook}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <FormControl fullWidth error={!!formErrors.eventName} disabled={isCreatingWebhook}>
                                <InputLabel id="events-label">{t("applications.eventName", "Event Name")}</InputLabel>
                                <Select
                                    labelId="events-label"
                                    name="eventName"
                                    value={newWebhook.eventName || ""}
                                    onChange={handleEventsChange}
                                >
                                    {availableEvents.map((event) => (
                                        <MenuItem key={event} value={event}>
                                            {event}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText>
                                    {formErrors.eventName ||
                                        t("applications.selectEvent", "Select an event to use as the Event Name")}
                                </FormHelperText>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                name="endpoint"
                                label={t("applications.endpoint", "Endpoint")}
                                value={newWebhook.endpoint || ""}
                                onChange={handleInputChange}
                                fullWidth
                                required
                                error={!!formErrors.endpoint}
                                helperText={formErrors.endpoint}
                                placeholder="https://example.com/api/webhook"
                                disabled={isCreatingWebhook}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth disabled={isCreatingWebhook}>
                                <InputLabel id="method-label">{t("applications.httpMethod", "HTTP Method")}</InputLabel>
                                <Select
                                    labelId="method-label"
                                    name="method"
                                    value={newWebhook.method || "POST"}
                                    onChange={handleMethodChange}
                                >
                                    {availableMethods.map((method) => (
                                        <MenuItem key={method} value={method}>
                                            {method}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                name="retryCount"
                                label={t("applications.retryCount", "Retry Count")}
                                type="number"
                                value={newWebhook.retryCount !== undefined ? newWebhook.retryCount : 3}
                                onChange={handleInputChange}
                                fullWidth
                                error={!!formErrors.retryCount}
                                helperText={formErrors.retryCount}
                                InputProps={{ inputProps: { min: 0 } }}
                                disabled={isCreatingWebhook}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                name="headers"
                                label={t("applications.headersJson", "Headers (JSON)")}
                                value={headersText}
                                onChange={handleHeadersChange}
                                fullWidth
                                multiline
                                rows={4}
                                error={!!formErrors.headers}
                                helperText={
                                    formErrors.headers ||
                                    t(
                                        "applications.headersHelp",
                                        'Enter headers as a JSON object, e.g. {"Content-Type": "application/json"}',
                                    )
                                }
                                placeholder='{"Content-Type": "application/json"}'
                                disabled={isCreatingWebhook}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} disabled={isCreatingWebhook}>
                        {t("applications.cancel", "Cancel")}
                    </Button>
                    <Button
                        onClick={handleCreateWebhook}
                        variant="contained"
                        color="primary"
                        startIcon={isCreatingWebhook ? <CircularProgress size={20} /> : <Save />}
                        disabled={isCreatingWebhook}
                    >
                        {t("applications.saveWebhook", "Save Webhook")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Webhook Confirmation Dialog */}
            <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel} aria-labelledby="delete-webhook-dialog-title">
                <DialogTitle id="delete-webhook-dialog-title">
                    {t("applications.confirmDeleteWebhook", "Confirm Delete Webhook")}
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        {t(
                            "applications.deleteWebhookWarning",
                            "Are you sure you want to delete this webhook? This action cannot be undone.",
                        )}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} disabled={isDeletingWebhook}>
                        {t("applications.cancel", "Cancel")}
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color="error"
                        variant="contained"
                        startIcon={isDeletingWebhook ? <CircularProgress size={20} /> : <Delete />}
                        disabled={isDeletingWebhook}
                    >
                        {t("applications.delete", "Delete")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
